// Stage 97: WARP — main compact widget.
//
// Honours the WARP-Bite handoff contract:
//   - per-turn ephemerality (a new turnId wipes prior bites + swats),
//   - three empty states: silent (success+0) / failure (red) / disabled,
//   - three kinds: OBSERVATION (teal) / QUESTION (gold) / OBJECTION (crimson),
//   - three swat tags: ACCEPT / REJECT / DEFER + implicit REJECT.
//
// Port of WARP-Zero-Design/ui_kits/widget/BiteWidget.jsx with:
//   - the internal useGadflyTurn reducer hook DROPPED (the host's
//     useGadflyTurn is the canonical subscriber; this widget consumes
//     its snapshot via the adapter and the `turnState` prop),
//   - the forwardRef + useImperativeHandle wrapping DROPPED (the store
//     is externally driven; no imperative handle is needed),
//   - the `producer` prop REMOVED,
//   - explicit TS typing.

import { useCallback, useEffect, useState } from 'react';
import type { BiteKind, SwatEntry, Bite } from '../../api/gadfly';
import { KIND_META, SWAT_META } from './kindMeta';
import { Icon } from './IconSprite';
import { WarpScene } from './WarpScene';
import type { WarpTurnState } from './types';
// Stage 201-X: WARP's single sanctioned reach into the Laughter module.
// useLaughterPulse is the ONLY file Warp/ may import from modules/Laughter
// (verifier Rule 5).
import { useLaughterPulse } from '../../modules/Laughter/useLaughterPulse';

interface BiteWidgetProps {
  /** REQUIRED — externally driven; no producer fallback. */
  turnState: WarpTurnState;
  /** What to show before the first turn arrives. */
  initialEmptyState?: 'disabled' | 'silent' | 'failure' | null;
  compact?: boolean;
  title?: string;
  subtitle?: string;
  /** Optional — fires from operator-side affordances inside WarpTab. */
  onTestBite?: (kind: BiteKind) => void;
  /** Optional — fires from operator-side affordances inside WarpTab. */
  onTestTurn?: (mode: string) => void;
}

export function BiteWidget({
  turnState,
  initialEmptyState = 'disabled',
  compact = false,
  title = 'WARP / ZERO',
  subtitle = 'Gadfly Bite stream',
  onTestBite,
  onTestTurn,
}: BiteWidgetProps): JSX.Element {
  const [turnsSeen, setTurnsSeen] = useState(0);
  useEffect(() => {
    if (turnState.turnId) setTurnsSeen((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnState.turnId]);

  // Stage 201-X: subscribe to the Laughter pulse store. A bite is
  // "laugh-fired" when the current pulse's turnId matches this turn AND
  // the bite's index appears in firedBiteIndices. useCallback keeps the
  // predicate identity stable except when the pulse / turn changes, so
  // WarpScene's data-attr effect only re-runs when it actually matters.
  const pulse = useLaughterPulse();
  const laughFiredFor = useCallback(
    (biteIndex: number): boolean =>
      pulse.turnId === turnState.turnId &&
      pulse.firedBiteIndices.includes(biteIndex),
    [pulse.turnId, pulse.firedBiteIndices, turnState.turnId],
  );

  const {
    turnId,
    bites,
    swats,
    failed,
    failureReason,
    durationMs,
    stripeRatio,
    turnEnded,
  } = turnState;

  const emptyState: 'failure' | 'silent' | 'disabled' | null =
    turnId === null
      ? initialEmptyState
      : failed
        ? 'failure'
        : bites.length === 0
          ? 'silent'
          : null;

  const taggedCount = Object.keys(swats).length;
  const acceptCount = Object.values(swats).filter(
    (s) => s.tag === 'ACCEPT',
  ).length;
  const deferCount = Object.values(swats).filter(
    (s) => s.tag === 'DEFER',
  ).length;
  const rejectCount = Object.values(swats).filter(
    (s) => s.tag === 'REJECT',
  ).length;

  const kindTallies: Record<BiteKind, number> = {
    OBSERVATION: 0,
    QUESTION: 0,
    OBJECTION: 0,
  };
  bites.forEach((b) => {
    kindTallies[b.kind] = (kindTallies[b.kind] || 0) + 1;
  });

  const W = compact ? 360 : 420;
  const SCENE_W = W - 20;
  const SCENE_H = compact ? 170 : 220;

  const turnIdShort = turnId ? `${turnId.slice(0, 8)}…` : '—';

  return (
    <div
      className={`wz-panel ${compact ? 'is-compact' : ''}`}
      style={{ width: W }}
    >
      {/* HEADER ---------------------------------------------- */}
      <header className="wz-head">
        <span className="wz-glyph">
          <Icon id="glyph" size={18} />
        </span>
        <div className="wz-head-text">
          <div className="wz-title">{title}</div>
          <div className="wz-subtitle">{subtitle}</div>
        </div>
        <div className="wz-kind-legend" aria-label="kind legend">
          <span className="wz-kchip k-OBSERVATION" title="Observation">
            <Icon id="observation" size={10} />
            {kindTallies.OBSERVATION}
          </span>
          <span className="wz-kchip k-QUESTION" title="Question">
            <Icon id="question" size={10} />
            {kindTallies.QUESTION}
          </span>
          <span className="wz-kchip k-OBJECTION" title="Objection">
            <Icon id="objection" size={10} />
            {kindTallies.OBJECTION}
          </span>
        </div>
      </header>

      {/* STATUS LINE ----------------------------------------- */}
      <div className="wz-status">
        <span
          className={`wz-dot ${failed ? 'is-error' : ''} ${
            emptyState === 'disabled' ? 'is-off' : ''
          }`}
        />
        <span>
          TURN&nbsp;<span className="wz-glyph-tri">▸</span>&nbsp;
          <b>{turnIdShort}</b>
        </span>
        <span className="wz-sep">·</span>
        <span>
          <b>{bites.length}</b>/20&nbsp;BITES
        </span>
        <span className="wz-sep">·</span>
        <span>
          <b>{taggedCount}</b>&nbsp;SWAT
        </span>
        <span className="wz-spacer" />
        <span
          className="wz-meta-pill"
          title={`stripe ${
            stripeRatio
              ? `${stripeRatio.keep}/${stripeRatio.keep + stripeRatio.cut}`
              : '—'
          }`}
        >
          {stripeRatio
            ? `${stripeRatio.keep}-of-${stripeRatio.keep + stripeRatio.cut}`
            : '—'}
        </span>
        <span className="wz-meta-pill">
          {durationMs != null ? `${durationMs}ms` : '—'}
        </span>
      </div>

      {/* WARP SCENE ----------------------------------------- */}
      <div className="wz-scene-wrap">
        <WarpScene
          width={SCENE_W}
          height={SCENE_H}
          bites={bites}
          swats={swats}
          turnId={turnId}
          failed={failed}
          failureReason={failureReason}
          emptyState={emptyState}
          laughFiredFor={laughFiredFor}
        />
        <span className="wz-scene-badge">
          TURNS&nbsp;<b>{turnsSeen}</b>
        </span>
      </div>

      {/* BITE LIST — inspector-style reference layout -------- */}
      <div className="wz-bites">
        {bites.length === 0 ? (
          <EmptyState mode={emptyState} reason={failureReason} />
        ) : null}
        {bites.map((b) => {
          const swat = swats[b.index];
          const implicit = !swat && turnEnded;
          return (
            <BiteRow
              key={b.index}
              bite={b}
              swat={swat}
              implicit={implicit}
              laughFired={laughFiredFor(b.index)}
            />
          );
        })}
      </div>

      {/* TURN END FOOTER ------------------------------------- */}
      {bites.length > 0 ? (
        <div className="wz-turn-foot">
          {turnEnded ? (
            <span>
              <span className="wz-glyph-tri">▸</span> turn closed ·{' '}
              {acceptCount} accept · {deferCount} defer ·{' '}
              {rejectCount + (bites.length - taggedCount)} reject (incl.
              implicit)
            </span>
          ) : (
            <span>
              <span className="wz-pulse-dot" /> turn live · awaiting more
              swats
            </span>
          )}
        </div>
      ) : null}

      {/* TEST CONTROLS (host-side affordance) ---------------- */}
      {onTestBite || onTestTurn ? (
        <div className="wz-controls">
          {onTestBite ? (
            <>
              <button
                className="wz-btn-kind k-OBSERVATION"
                onClick={() => onTestBite('OBSERVATION')}
                title="Inject OBSERVATION bite"
              >
                <Icon id="observation" size={10} />
                OBS
              </button>
              <button
                className="wz-btn-kind k-QUESTION"
                onClick={() => onTestBite('QUESTION')}
                title="Inject QUESTION bite"
              >
                <Icon id="question" size={10} />
                QST
              </button>
              <button
                className="wz-btn-kind k-OBJECTION"
                onClick={() => onTestBite('OBJECTION')}
                title="Inject OBJECTION bite"
              >
                <Icon id="objection" size={10} />
                OBJ
              </button>
            </>
          ) : null}
          {onTestTurn ? (
            <div className="wz-spacer-flex">
              <button
                className="wz-btn-sec wz-btn-turn"
                onClick={() => onTestTurn('normal')}
                title="Trigger new turn"
              >
                <Icon id="turn" size={10} />
                NEW TURN
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* HINT FOOTER ----------------------------------------- */}
      <div className="wz-hint">
        <span>
          <b>OBS</b> teal · neutral
        </span>
        <span>
          <b>QST</b> gold · curious
        </span>
        <span>
          <b>OBJ</b> crimson · push-back
        </span>
      </div>
    </div>
  );
}

// --- BiteRow — single bite + swat row ----------------------------------
interface BiteRowProps {
  bite: Bite;
  swat: SwatEntry | undefined;
  implicit: boolean;
  /** Stage 201-X: true when a Laughter engram fired for this bite this turn. */
  laughFired?: boolean;
}

function BiteRow({
  bite,
  swat,
  implicit,
  laughFired = false,
}: BiteRowProps): JSX.Element {
  const meta = KIND_META[bite.kind] || KIND_META.OBSERVATION;
  const swatMeta = swat ? SWAT_META[swat.tag] : null;
  const cls = [
    'wz-bite',
    `is-${bite.kind}`,
    swat ? `is-swat-${swat.tag}` : '',
    implicit ? 'is-implicit' : '',
    laughFired ? 'is-laugh-fired' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const styleVars = {
    '--bite-color': meta.color,
    '--bite-rgb': meta.rgb,
  } as React.CSSProperties;
  return (
    <div className={cls} style={styleVars}>
      <div className="wz-bite-head">
        <span className="wz-bite-idx">
          [{String(bite.index).padStart(2, '0')}]
        </span>
        <span className="wz-bite-kind">
          <Icon id={meta.icon} size={10} />
          {bite.kind}
        </span>
        <span className="wz-bite-text">{bite.text}</span>
      </div>
      {swat && swatMeta ? (
        <div
          className="wz-bite-swat"
          style={{ '--swat-color': swatMeta.color } as React.CSSProperties}
        >
          <span className="wz-glyph-tri">▸</span>
          <Icon id={swatMeta.icon} size={10} />
          <b>{swatMeta.label}</b>
          {laughFired ? (
            <span
              className="wz-laugh-glyph"
              aria-label="laughter fired"
              title="laughter engram fired"
            >
              ✦
            </span>
          ) : null}
          {swat.reason ? (
            <span className="wz-bite-reason">— &ldquo;{swat.reason}&rdquo;</span>
          ) : null}
        </div>
      ) : null}
      {implicit && !swat ? (
        <div className="wz-bite-swat is-implicit">
          <span className="wz-glyph-tri">▸</span>
          <span className="wz-dim">(untagged — implicit REJECT)</span>
        </div>
      ) : null}
    </div>
  );
}

// --- EmptyState — silent / failure / disabled / awaiting ---------------
interface EmptyStateProps {
  mode: 'failure' | 'silent' | 'disabled' | null;
  reason: string | null;
}

function EmptyState({ mode, reason }: EmptyStateProps): JSX.Element | null {
  if (mode === 'failure') {
    return (
      <div className="wz-empty is-failure">
        <Icon id="warning" size={14} />
        <div>
          <div className="wz-empty-title">gadfly failure</div>
          <div className="wz-empty-sub">{reason || 'unknown error'}</div>
        </div>
      </div>
    );
  }
  if (mode === 'silent') {
    return (
      <div className="wz-empty is-silent">
        <span className="wz-glyph-tri">▸</span>
        <div>
          <div className="wz-empty-title">silent turn</div>
          <div className="wz-empty-sub">
            gadfly ran clean · produced no parseable bites
          </div>
        </div>
      </div>
    );
  }
  if (mode === 'disabled') {
    return (
      <div className="wz-empty is-disabled">
        <span className="wz-glyph-tri">◦</span>
        <div>
          <div className="wz-empty-title">gadfly offline</div>
          <div className="wz-empty-sub">awaiting first turn · no events yet</div>
        </div>
      </div>
    );
  }
  return null;
}
